/**
 * Unified Pricing Types
 * Centralized type definitions for the pricing system
 *
 * Note: All prices are stored excluding VAT. VAT is calculated separately when needed.
 */

import { CUSTOMER_TYPE_IDS } from "@/lib/constants/customer-types";
import { PRICE_TYPE_IDS } from "@/lib/constants/price-types";

// ============================================================================
// Core Types
// ============================================================================

export type CustomerType = "private" | "corporate";
export type PriceType = "fixed" | "per_person";
export type ModifierType = "discount" | "surcharge" | "percentage";

// ============================================================================
// Pricing Breakdown - Single definition for all pricing calculations
// ============================================================================

export interface PricingBreakdown {
  baseAmount: number; // Amount excluding VAT
  vatAmount: number; // VAT amount
  vatRate: number; // VAT percentage
  total: number; // Total including VAT (calculated)
  currency?: string;

  // Display helpers
  displayAmount?: number; // Amount to display (based on customer type)
  displayLabel?: string; // Label for display (e.g., "incl. VAT" or "excl. VAT")

  // Per-person calculations (when applicable)
  pricePerPerson?: number; // Price per person excluding VAT
}

// ============================================================================
// Add-On Related Types - Unified definition
// ============================================================================

export interface AddOn {
  id: number;
  add_on_id: number;
  name?: string;
  description?: string;
  quantity: number;
  price: number; // Unit price excluding VAT
  vatPercentage: number;
  type: "per_person" | "fixed";

  // Calculated fields for add-ons (stored in booking_add_ons table)
  total_price_excl_vat?: number;
  vat_amount?: number;
  vat_rate?: number;

  // UI fields
  imageUrl?: string;
  is_archived?: boolean;
}

// Booking-specific add-on (includes database relation)
export interface BookingAddOn extends AddOn {
  booking_id?: number;
  price_type_id?: number;
  assigned_pricing_id?: number;

  // Nested pricing data from joins
  add_ons?: {
    pricings?: {
      price: number; // Always excluding VAT
      vat_percentage: number;
    };
    pricing?: {
      // Legacy support
      price: number;
      vat_percentage: number;
    };
  };
}

// ============================================================================
// Shop Item Types
// ============================================================================

export interface ShopItem {
  id: number;
  shop_item_id: number;
  name?: string;
  quantity: number;
  price: number; // Unit price excluding VAT
  vatPercentage: number;

  // Calculated fields
  total_price_excl_vat?: number;
  vat_amount?: number;
}

export interface BookingShopItem extends ShopItem {
  booking_id?: number;

  // Nested pricing data
  shop_items?: {
    pricings?: {
      price: number; // Always excluding VAT
      vat_percentage: number;
    };
    pricing?: {
      price: number;
      vat_percentage: number;
    };
  };
}

// ============================================================================
// Show & Modifier Types
// ============================================================================

export interface ShowSelection {
  showId: number;
  venueId: number;
  timeSlot: string;
  date: string;
  dayOfWeek?: number;
  pricePointId?: number;
  basePrice?: number; // Override price (excluding VAT)
  vatRate?: number; // Override VAT percentage
}

export interface PriceModifier {
  id: number;
  name: string;
  modifier_type: ModifierType;
  modifier_value: number;
  start_date: string;
  end_date: string;
  applicable_days?: number[];
  time_slots?: string[];
  customer_type_ids?: number[];
  role_ids?: number[];
  venue_id?: number;
}

export interface ShowPricingData {
  showId: number;
  price: number; // Base price excluding VAT
  vatRate: number;
  currency: string;
  modifier?: PriceModifier;
  modifierAmount?: number;

  // Database IDs for reference
  showTimeSlotPricingId?: number;
  pricePointId?: number;
  pricingId?: number;
}

// ============================================================================
// Booking Types - Using new pricing fields
// ============================================================================

export interface BookingData {
  // Core fields
  id?: number;
  venue_id?: number;
  customer_type_id: number;
  price_type_id?: number;
  participant_count: number;

  // New pricing fields (from database) - all excluding VAT
  base_amount_excl_vat?: number;
  vat_amount?: number;
  vat_rate?: number;

  // Per-person/fixed mode prices (excluding VAT)
  per_person_mode_price_excluding_vat?: number;
  fixed_mode_price_excluding_vat?: number;

  // Relations
  booking_add_ons?: BookingAddOn[];
  booking_shop_items?: BookingShopItem[];
  shows?: ShowSelection[];
}

// ============================================================================
// Calculation Results
// ============================================================================

export interface BookingTotals {
  shows: PricingBreakdown;
  addOns: PricingBreakdown;
  shopItems: PricingBreakdown;

  // Aggregates
  subtotal: number; // Total excluding VAT
  totalVat: number; // Total VAT amount
  grandTotal: number; // Total including VAT (calculated)

  // Display values
  displayAmount: number; // Based on customer type (excl VAT for corporate, incl for private)
  displayLabel: string; // "incl. VAT" or "excl. VAT"

  // Modifiers applied
  modifiersApplied?: Array<{
    name: string;
    amount: number;
    type: ModifierType;
  }>;
}

// ============================================================================
// API Request/Response Types
// ============================================================================

export interface PricingRequest {
  // Basic info
  unitPrice: number; // Always excluding VAT
  vatPercentage: number;
  participants: number;
  customerType: CustomerType;
  priceType: PriceType;

  // Optional modifiers
  priceModifier?: {
    type: ModifierType;
    value: number;
  };

  // Optional add-ons
  addOns?: Array<{
    price: number; // Unit price excluding VAT
    quantity: number;
    vatPercentage: number;
    type?: "per_person" | "fixed";
  }>;

  currency?: string;
}

export interface ShowPricingRequest {
  showId: number;
  venueId: number;
  date: string;
  timeSlot: string;
  dayOfWeek?: string | number;
  participants: number;
  customerTypeId: number;
  userRoleId?: number;
  priceType?: PriceType;
}

export interface ShowPricingResult {
  basePrice: number; // Base price excluding VAT
  vatRate: number;
  pricePerPerson: number; // Per person price excluding VAT
  totalPrice: number; // Total including VAT (calculated for display)
  currency: string;
  modifier?: PriceModifier;
  modifierAmount: number;
  displayLabel: string;
  breakdown: PricingBreakdown;
  pricingIds: {
    showTimeSlotPricingId?: number;
    pricePointId?: number;
    pricingId?: number;
  };

  // Database storage fields (all excluding VAT)
  perPersonModeExclVat: number;
  fixedModeExclVat: number;
  priceType: PriceType;
  priceTypeId: number;
}

export interface BookingRecalculateRequest {
  bookingId: number;
  participants?: number;
  customerTypeId?: number;
  addOns?: Array<{
    addOnId: number;
    quantity: number;
  }>;
}

export interface BookingRecalculateResult {
  bookingId: number;
  shows: ShowPricingResult[];
  addOns: Array<{
    addOnId: number;
    quantity: number;
    unitPrice: number; // Excluding VAT
    totalPriceExclVat: number; // Excluding VAT
    vatAmount: number;
  }>;
  totals: BookingTotals;
  breakdown: PricingBreakdown;
}

// ============================================================================
// Helper Type Guards & Utilities
// ============================================================================

export function isPrivateCustomer(customerTypeId: number): boolean {
  return customerTypeId === CUSTOMER_TYPE_IDS.PRIVATE;
}

export function isCorporateCustomer(customerTypeId: number): boolean {
  return customerTypeId === CUSTOMER_TYPE_IDS.CORPORATE;
}

export function getCustomerType(customerTypeId: number): CustomerType {
  return isCorporateCustomer(customerTypeId) ? "corporate" : "private";
}

export function isPricePerPerson(priceTypeId?: number): boolean {
  return priceTypeId === PRICE_TYPE_IDS.PER_PERSON;
}

export function getPriceType(priceTypeId?: number): PriceType {
  return isPricePerPerson(priceTypeId) ? "per_person" : "fixed";
}

/**
 * Calculate VAT-inclusive price from exclusive price
 * Used for display purposes only
 * @param priceExclVat Price excluding VAT
 * @param vatRate VAT percentage (e.g., 25 for 25%)
 */
export function addVatToPrice(priceExclVat: number, vatRate: number): number {
  return priceExclVat * (1 + vatRate / 100);
}

/**
 * Calculate VAT amount from exclusive price
 * @param priceExclVat Price excluding VAT
 * @param vatRate VAT percentage (e.g., 25 for 25%)
 */
export function calculateVatAmount(
  priceExclVat: number,
  vatRate: number,
): number {
  return priceExclVat * (vatRate / 100);
}
